<script>
import { mapActions } from "vuex";
import { mapGetters } from "vuex";
import { Xhr } from "tortilla";

// Utilities

/**
 * Transforms a string to camel case
 *
 * @param {String} val - The string to transform
 * @returns {String} - The transformed string
 */
const toCamelCase = val =>
    val
        .split("-")
        .join(" ")
        .split(" ")
        .map((str, i) => {
            return i === 0
                ? str.charAt(0).toLowerCase() + str.slice(1)
                : str.charAt(0).toUpperCase() + str.slice(1);
        })
        .join("");

/**
 * Capitalizes a string
 *
 * @param {String} val - The string to transform
 * @returns {String} - The transformed string
 */
const toCapitalize = val =>
    val
        .split("-")
        .map((str, i) => {
            return str.charAt(0).toUpperCase() + str.slice(1);
        })
        .join(" ");

/**
 * Transforms a string to uppercase snake case
 *
 * @param {String} val - The string to transform
 * @returns {String} - The transformed string
 */
const toUpperSnakeCase = val =>
    val
        .toUpperCase()
        .split("-")
        .join("_");

/**
 * Groups an array of objects by a key
 *
 * @param {Array} items - Array to group
 * @param {Function} keyGetter - Ex: `item => item.prop`
 * @returns {Array} - The grouped array of arrays
 */
const groupBy = (items, keyGetter) => {
    const map = new Map();
    items.forEach(item => {
        const key = toCamelCase(keyGetter(item));
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return Array.from(map.values());
};

export default {
    props: ['plans'],
    data: () => ({
        parameters: {
            is_bold_series_home: '',
        },
        isLoading: false,
        modalOpen: "",
        selectedImages: [],
        galleries: [],
        homes: [],
        groupedPlans: [],
        currentSort: {
            sort: 'name',
            desc: 'ascending',
        },
        sortByList: [
            {key: 'name-ascending', value: 'Name: Low to High'},
            {key: 'name-descending', value: 'Name: High to Low'},
            {key: 'sqft-ascending', value: 'Sq. Ft: Low to High'},
            {key: 'sqft-descending', value: 'Sq. Ft: High to Low'},
            {key: 'beds-ascending', value: 'Beds: Low to High'},
            {key: 'beds-descending', value: 'Beds: High to Low'},
            {key: 'baths-ascending', value: 'Baths: Low to High'},
            {key: 'baths-descending', value: 'Baths: High to Low'},
        ],
        sortBy: 'name-ascending',
    }),
    computed: {
        /*
        sortOptions() {
            return window.innerWidth < 768 
                ? ['Plan Name', 'Beds', 'Baths', 'Sq. Ft.'] // mobile
                : ['Plan Name (A - Z)', 'Beds', 'Baths', 'Sq. Ft.'];
        },
        */
        bedOptions() {
            return [
                ['3+', 'beds', '3'],
                ['4+', 'beds', '4'],
                ['5+', 'beds', '5'],
            ];
        },
        bathOptions() {
            return [
                ['2+', 'baths', '2'],
                ['3+', 'baths', '3'],
            ];
        },
        sqftMinOptions() {
            return [
                ['1,000', 'sqft-min', '1000'],
                ['2,000', 'sqft-min', '2000'],
            ];
        },
        sqftMaxOptions() {
            return [
                ['2,000', 'sqft-max', '2000'],
                ['3,000', 'sqft-max', '3000'],
            ];
        },
        sortedHomes() {
            return this.groupedPlans;
        }
    },
    watch: {
        'sortBy': {
            handler() {
                this.sort();
            }
        },
        'parameters.is_bold_series_home': {
            handler() {
                this.shouldDisplay();
            }
        },
    },
    mounted() {
        this.groupedPlans = this.plans;
        this.$eventsBus.$on("select-option", this.handleSelect);
        this.$eventsBus.$on("unset-option", this.handleUnselect);
    },
    beforeDestroy() {
        this.$eventsBus.$off("select-option", this.handleSelect);
        this.$eventsBus.$off("unset-option", this.handleUnselect);
    },
    methods: {
        ...mapActions({
            put: "slick/put",
            videoPut: "modal/put"
        }),
        handleSelect(event) {
            const optionKey = event[1];
            const value = optionKey === 'city' ? event[0] : event[2];
            this.parameters[optionKey] = `${value}`;
            this.shouldDisplay();
        },
        handleUnselect(event) {
            const optionKey = event[2];
            const value = event[1];
            delete this.parameters[optionKey];
            this.shouldDisplay();
        },
        closeModal() {
            this.modalOpen = "";
        },
        /*
        sort(keyname, desc) {
            this.currentSort.sort = keyname;
            this.currentSort.desc = desc;
            this.handleDataSort();
        },
        handleDataSort() {
            return this.groupedPlans = this.groupedPlans.sort((a, b) => {
                // Plan name desc to asc
                if (this.currentSort.sort == 'Plan Name (A - Z)' && this.currentSort.desc) {
                    return a.name < b.name ? -1 : 1;
                }
                // Plan name asc to desc
                if (this.currentSort.sort == 'Plan Name (A - Z)' && !this.currentSort.desc) {
                    return a.name > b.name ? -1 : 1;
                }
                // Beds high to low
                if (this.currentSort.sort == 'Beds' && this.currentSort.desc) {
                    return a.bedsRange < b.bedsRange ? -1 : 1;
                }
                // Beds low to high
                if (this.currentSort.sort == 'Beds' && !this.currentSort.desc) {
                    return a.bedsRange > b.bedsRange ? -1 : 1;
                }
                // Baths high to low
                if (this.currentSort.sort == 'Baths' && this.currentSort.desc) {
                    return a.bathsRange < b.bathsRange ? -1 : 1;
                }
                // Baths low to high
                if (this.currentSort.sort == 'Baths' && !this.currentSort.desc) {
                    return a.bathsRange > b.bathsRange ? -1 : 1;
                }
                // Sq. Ft. high to low
                if (this.currentSort.sort == 'Sq. Ft.' && this.currentSort.desc) {
                    const sqftDescA = Number(a.sqftRange.split(',').join(''));
                    const sqftDescB = Number(b.sqftRange.split(',').join(''));
                    return sqftDescA < sqftDescB ? -1 : 1;
                }
                // Sq. Ft. low to high
                if (this.currentSort.sort == 'Sq. Ft.' && !this.currentSort.desc) {
                    const sqftAscA = Number(a.sqftRange.split(',').join(''));
                    const sqftAscB = Number(b.sqftRange.split(',').join(''));
                    return sqftAscA > sqftAscB ? -1 : 1;
                }

                return 0;
            });
        },
        */
        sort() {
            this.currentSort.sort = this.sortBy ? this.sortBy.split('-')[0] : null;
            this.currentSort.desc = this.sortBy ? this.sortBy.split('-')[1] : null;
            this.handleDataSort();
        },
        handleDataSort() {
            return this.groupedPlans = this.groupedPlans.sort((a, b) => {
                // Plan name desc to asc
                if (this.currentSort.sort == 'name' && this.currentSort.desc === 'ascending') {
                    return a.name < b.name ? -1 : 1;
                }
                // Plan name asc to desc
                if (this.currentSort.sort == 'name' && this.currentSort.desc === 'descending') {
                    return a.name > b.name ? -1 : 1;
                }
                // Beds high to low
                if (this.currentSort.sort == 'beds' && this.currentSort.desc === 'ascending') {
                    return a.bedsRange < b.bedsRange ? -1 : 1;
                }
                // Beds low to high
                if (this.currentSort.sort == 'beds' && this.currentSort.desc === 'descending') {
                    return a.bedsRange > b.bedsRange ? -1 : 1;
                }
                // Baths high to low
                if (this.currentSort.sort == 'baths' && this.currentSort.desc === 'ascending') {
                    return a.bathsRange < b.bathsRange ? -1 : 1;
                }
                // Baths low to high
                if (this.currentSort.sort == 'baths' && this.currentSort.desc === 'descending') {
                    return a.bathsRange > b.bathsRange ? -1 : 1;
                }
                // Sq. Ft. high to low
                if (this.currentSort.sort == 'sqft' && this.currentSort.desc === 'ascending') {
                    const sqftDescA = Number(a.sqftRange.split(',').join(''));
                    const sqftDescB = Number(b.sqftRange.split(',').join(''));
                    return sqftDescA < sqftDescB ? -1 : 1;
                }
                // Sq. Ft. low to high
                if (this.currentSort.sort == 'sqft' && this.currentSort.desc === 'descending') {
                    const sqftAscA = Number(a.sqftRange.split(',').join(''));
                    const sqftAscB = Number(b.sqftRange.split(',').join(''));
                    return sqftAscA > sqftAscB ? -1 : 1;
                }

                return 0;
            });
        },
        shouldDisplay() {
            if (Object.keys(this.parameters).length) {
                return this.groupedPlans = this.plans.filter(item => {
                    if (this.matchesQuery(item)) {
                        return true;
                    }
                });
            } else {
                return this.groupedPlans = this.plans;
            }
        },
        matchesQuery(item) {
            if (this.parameters.beds) {
                if (item.bedsRange < this.parameters.beds) {
                    return false;
                }
            }
            if (this.parameters.baths) {
                if (item.bathsRange <  this.parameters.baths) {
                    return false;
                }
            }
            if (this.parameters['sqft-min'] && this.parameters['sqft-max']) {
                if ((item.sqft_low >= this.parameters['sqft-min']) && (item.sqft_low >= this.parameters['sqft-max'])) {
                    return false;
                }
            }
            if (this.parameters['sqft-min']) {
                if (item.sqft_low <= this.parameters['sqft-min']) {
                    return false;
                }
            }
            if (this.parameters['sqft-max']) {
                if (item.sqft_low >= this.parameters['sqft-max']) {
                    return false;
                }
            }
            if (this.parameters.is_bold_series_home) {
                if (!!item.is_bold_series_home !== this.parameters.is_bold_series_home) {
                    return false;
                }
            }
            return true;
        }
    }
};
</script>



// WEBPACK FOOTER //
// resources/components/pages/floorplans/floorplans-filter.vue